import type { PdfEdit } from "./types";

export type InterfaceLanguage = "sk" | "en";

export const INTERFACE_LANGUAGE_STORAGE_KEY = "pdf-lite-interface-language";

export interface UiCopy {
  language: InterfaceLanguage;
  languageControl: string;
  ready: string;
  processing: string;
  newText: string;
  page: (page: number) => string;
  scan: string;
  moveLayer: string;
  resizeLayer: (corner: string) => string;
  rotateLayer: string;
  highlight: string;
  redaction: string;
  text: string;
  colorLayer: string;
  layer: string;
  emptyText: string;
  show: string;
  hide: string;
  noLayers: string;
  noLayersHint: string;
  pageLayers: string;
  openPdf: string;
  loadingPdf: string;
  pagesLoaded: (count: number) => string;
  pdfOpenFailed: string;
  choosePdf: string;
  layerShown: string;
  layerHidden: string;
  preparingPage: string;
  automaticOcr: (page: number) => string;
  ocrPage: (page: number) => string;
  ocrFound: (count: number) => string;
  ocrNotFound: string;
  ocrFailed: string;
  creatingPdf: string;
  pdfDownloaded: string;
  exportFailed: string;
  pdfNeedsPage: string;
  pageRestored: (page: number) => string;
  pageDeleted: (page: number) => string;
  addNewPdf: string;
  discardPdfTitle: string;
  discardPdfMessage: string;
  cancel: string;
  discardAndContinue: string;
  exportPdf: string;
  undo: string;
  redo: string;
  selectTool: string;
  addText: string;
  highlightTool: string;
  redactTool: string;
  ocrActivePage: string;
  zoomOut: string;
  zoomIn: string;
  pages: string;
  layers: string;
  searchPage: string;
  edit: string;
  fonts: string;
  content: string;
  exportFont: string;
  maxSize: string;
  size: string;
  weight: string;
  fontStyle: string;
  fontWeightLabel: string;
  matchedFont: string;
  color: string;
  opacity: string;
  coverOriginal: string;
  autoFit: string;
  position: string;
  rotation: string;
  rotationDegrees: string;
  deleteEdit: string;
  selectEdit: string;
  selectEditHint: string;
  activePage: string;
  deleteRestorePage: string;
  fontEstimates: string;
  scannedPages: string;
  fontAnalyzerInfo: string;
  ocrEstimate: (confidence: number) => string;
  characters: (count: number) => string;
  fontPages: (pages: number[]) => string;
  awaitingAnalysis: string;
  awaitingAnalysisHint: string;
  ocrScans: string;
  ocrScansInfo: string;
  automaticOpenScan: string;
  documentLanguages: string;
  slovak: string;
  czech: string;
  english: string;
  textLayer: string;
  runOcrAgain: string;
  runOcr: string;
  rowsReady: (count: number) => string;
  recognizedText: string;
  firstOcrInfo: string;
  editsAndRows: (edits: number, rows: number) => string;
  sourceLabel: (source?: PdfEdit["source"]) => string;
  ocrProgress: (status: string) => string;
}

const ocrProgressMap = (language: InterfaceLanguage, status: string) => {
  const normalized = status.trim().toLowerCase();
  const sk: Record<string, string> = {
    "loading tesseract core": "Načítavam OCR jadro",
    "initializing tesseract": "Inicializujem OCR",
    "loading language traineddata": "Načítavam jazykové modely",
    "initializing api": "Pripravujem OCR",
    "recognizing text": "Rozpoznávam text",
    "zlepšujem kontrast skenu": "Zlepšujem kontrast skenu",
    "skúšam alternatívne rozloženie textu": "Skúšam alternatívne rozloženie textu",
    "oddeľujem text od pečiatky": "Oddeľujem text od pečiatky",
  };
  const en: Record<string, string> = {
    "loading tesseract core": "Loading OCR engine",
    "initializing tesseract": "Initializing OCR",
    "loading language traineddata": "Loading language models",
    "initializing api": "Preparing OCR",
    "recognizing text": "Recognizing text",
    "zlepšujem kontrast skenu": "Enhancing scan contrast",
    "skúšam alternatívne rozloženie textu": "Trying an alternative text layout",
    "oddeľujem text od pečiatky": "Separating text from the stamp",
  };
  return (language === "en" ? en : sk)[normalized] ?? status;
};

export const getUiCopy = (language: InterfaceLanguage): UiCopy => language === "en" ? {
  language: "en",
  languageControl: "Interface language",
  ready: "Ready",
  processing: "Processing…",
  newText: "New text",
  page: (page) => `Page ${page}`,
  scan: "scan",
  moveLayer: "Move layer",
  resizeLayer: (corner) => `Resize layer ${corner}`,
  rotateLayer: "Rotate layer",
  highlight: "Highlight",
  redaction: "Redaction",
  text: "Text",
  colorLayer: "Color",
  layer: "layer",
  emptyText: "Empty text",
  show: "Show",
  hide: "Hide",
  noLayers: "No layers",
  noLayersHint: "Add text, color or a redaction.",
  pageLayers: "Page layers",
  openPdf: "Open PDF",
  loadingPdf: "Loading PDF…",
  pagesLoaded: (count) => `${count} ${count === 1 ? "page" : "pages"} loaded`,
  pdfOpenFailed: "The PDF could not be opened.",
  choosePdf: "Select a PDF file.",
  layerShown: "Layer shown",
  layerHidden: "Layer hidden",
  preparingPage: "Preparing page",
  automaticOcr: (page) => `Automatically recognizing scanned page ${page}…`,
  ocrPage: (page) => `OCR page ${page}…`,
  ocrFound: (count) => `OCR found ${count} text rows. Click a green outline or a row on the right.`,
  ocrNotFound: "OCR did not find readable text. Try a better scan or another language.",
  ocrFailed: "OCR could not be completed.",
  creatingPdf: "Creating a new PDF…",
  pdfDownloaded: "The PDF was created and downloaded",
  exportFailed: "Export failed.",
  pdfNeedsPage: "The PDF must contain at least one page.",
  pageRestored: (page) => `Page ${page} restored`,
  pageDeleted: (page) => `Page ${page} will be removed`,
  addNewPdf: "Add a new PDF (⌘O)",
  discardPdfTitle: "Remove the current PDF?",
  discardPdfMessage: "The PDF and all edits will be permanently removed from this browser.",
  cancel: "Cancel",
  discardAndContinue: "Remove and continue",
  exportPdf: "Export PDF",
  undo: "Undo (⌘Z)",
  redo: "Redo (⇧⌘Z)",
  selectTool: "Select and edit text",
  addText: "Add text",
  highlightTool: "Highlight",
  redactTool: "Permanent redaction",
  ocrActivePage: "OCR active page",
  zoomOut: "Zoom out",
  zoomIn: "Zoom in",
  pages: "Pages",
  layers: "Layers",
  searchPage: "Find a page",
  edit: "Edit",
  fonts: "Fonts",
  content: "Content",
  exportFont: "Export font",
  maxSize: "Max. size",
  size: "Size",
  weight: "Weight",
  fontStyle: "Style",
  fontWeightLabel: "Font weight",
  matchedFont: "Font matched to the scan",
  color: "Color",
  opacity: "Opacity",
  coverOriginal: "Cover original content",
  autoFit: "Automatically fit text to the field",
  position: "Position",
  rotation: "Rotation",
  rotationDegrees: "Rotation in degrees",
  deleteEdit: "Delete edit",
  selectEdit: "Select text or an edit",
  selectEditHint: "Click text in the PDF or use one of the tools above.",
  activePage: "Active page",
  deleteRestorePage: "Delete / restore page",
  fontEstimates: "fonts and OCR estimates",
  scannedPages: "scanned pages",
  fontAnalyzerInfo: "For scans, the analyzer estimates family, weight, style and size from the letter shapes. The estimate is applied automatically when you click an OCR row.",
  ocrEstimate: (confidence) => `OCR estimate · match ${confidence}%`,
  characters: (count) => `${count} characters`,
  fontPages: (pages) => `Pages ${pages.join(", ")}`,
  awaitingAnalysis: "Waiting for analysis",
  awaitingAnalysisHint: "Scan estimates appear after OCR finishes.",
  ocrScans: "Scan OCR",
  ocrScansInfo: "Renders the active page at a higher resolution and creates clickable text rows.",
  automaticOpenScan: "Automatically recognize an opened scan",
  documentLanguages: "Document languages",
  slovak: "Slovak",
  czech: "Czech",
  english: "English",
  textLayer: "Text layer",
  runOcrAgain: "Run OCR again",
  runOcr: "Run OCR on this page",
  rowsReady: (count) => `${count} ${count === 1 ? "row is" : "rows are"} ready to edit.`,
  recognizedText: "Recognized text",
  firstOcrInfo: "The first OCR run may download language models. The browser then uses them from its cache.",
  editsAndRows: (edits, rows) => `${edits} edits · ${rows} OCR rows`,
  sourceLabel: (source) => source === "new" ? "new" : source === "native" ? "PDF text" : source === "ocr" ? "OCR" : "layer",
  ocrProgress: (status) => ocrProgressMap("en", status),
} : {
  language: "sk",
  languageControl: "Jazyk rozhrania",
  ready: "Pripravené",
  processing: "Spracúvam…",
  newText: "Nový text",
  page: (page) => `Strana ${page}`,
  scan: "sken",
  moveLayer: "Presunúť vrstvu",
  resizeLayer: (corner) => `Zmeniť veľkosť ${corner}`,
  rotateLayer: "Otočiť vrstvu",
  highlight: "Zvýraznenie",
  redaction: "Redakcia",
  text: "Text",
  colorLayer: "Farba",
  layer: "vrstva",
  emptyText: "Prázdny text",
  show: "Zobraziť",
  hide: "Skryť",
  noLayers: "Žiadne vrstvy",
  noLayersHint: "Pridaj text, farbu alebo redakciu.",
  pageLayers: "Vrstvy strany",
  openPdf: "Otvoriť PDF",
  loadingPdf: "Načítavam PDF…",
  pagesLoaded: (count) => `${count} ${count === 1 ? "strana načítaná" : "strán načítaných"}`,
  pdfOpenFailed: "PDF sa nepodarilo otvoriť.",
  choosePdf: "Vyber PDF súbor.",
  layerShown: "Vrstva zobrazená",
  layerHidden: "Vrstva skrytá",
  preparingPage: "Pripravujem stranu",
  automaticOcr: (page) => `Automaticky rozpoznávam skenovanú stranu ${page}…`,
  ocrPage: (page) => `OCR strany ${page}…`,
  ocrFound: (count) => `OCR našlo ${count} textových riadkov. Klikni na zelený rámček alebo riadok vpravo.`,
  ocrNotFound: "OCR nenašlo čitateľný text. Skús kvalitnejší sken alebo iný jazyk.",
  ocrFailed: "OCR sa nepodarilo dokončiť.",
  creatingPdf: "Vytváram nové PDF…",
  pdfDownloaded: "PDF bolo vytvorené a stiahnuté",
  exportFailed: "Export zlyhal.",
  pdfNeedsPage: "PDF musí obsahovať aspoň jednu stranu.",
  pageRestored: (page) => `Strana ${page} obnovená`,
  pageDeleted: (page) => `Strana ${page} bude odstránená`,
  addNewPdf: "Pridať nové PDF (⌘O)",
  discardPdfTitle: "Odstrániť aktuálne PDF?",
  discardPdfMessage: "PDF a všetky úpravy sa natrvalo odstránia z tohto prehliadača.",
  cancel: "Zrušiť",
  discardAndContinue: "Odstrániť a pokračovať",
  exportPdf: "Exportovať PDF",
  undo: "Späť (⌘Z)",
  redo: "Znova (⇧⌘Z)",
  selectTool: "Výber a úprava textu",
  addText: "Pridať text",
  highlightTool: "Zvýrazniť",
  redactTool: "Trvalá redakcia",
  ocrActivePage: "OCR aktívnej strany",
  zoomOut: "Oddialiť",
  zoomIn: "Priblížiť",
  pages: "Strany",
  layers: "Vrstvy",
  searchPage: "Hľadať stranu",
  edit: "Upraviť",
  fonts: "Fonty",
  content: "Obsah",
  exportFont: "Font exportu",
  maxSize: "Max. veľkosť",
  size: "Veľkosť",
  weight: "Hrúbka",
  fontStyle: "Rez",
  fontWeightLabel: "Hrúbka písma",
  matchedFont: "Font prispôsobený podľa skenu",
  color: "Farba",
  opacity: "Priehľadnosť",
  coverOriginal: "Prekryť pôvodný obsah",
  autoFit: "Automaticky prispôsobiť text poľu",
  position: "Pozícia",
  rotation: "Rotácia",
  rotationDegrees: "Rotácia v stupňoch",
  deleteEdit: "Odstrániť úpravu",
  selectEdit: "Vyber text alebo úpravu",
  selectEditHint: "Klikni na text v PDF alebo použi jeden z nástrojov hore.",
  activePage: "Aktívna strana",
  deleteRestorePage: "Odstrániť / obnoviť stranu",
  fontEstimates: "fontov a OCR odhadov",
  scannedPages: "skenovaných strán",
  fontAnalyzerInfo: "Pri skene analyzátor odhadne rodinu, hrúbku, rez a veľkosť z tvaru písmen. Odhad sa po kliknutí na OCR riadok použije automaticky.",
  ocrEstimate: (confidence) => `OCR odhad · zhoda ${confidence}%`,
  characters: (count) => `${count} znakov`,
  fontPages: (pages) => `Strany ${pages.join(", ")}`,
  awaitingAnalysis: "Čakám na analýzu",
  awaitingAnalysisHint: "Na skene sa odhady zobrazia po dokončení OCR.",
  ocrScans: "OCR skenov",
  ocrScansInfo: "Vyrenderuje aktívnu stranu vo vyššom rozlíšení a vytvorí klikateľné textové riadky.",
  automaticOpenScan: "Automaticky rozpoznať otvorený sken",
  documentLanguages: "Jazyky dokumentu",
  slovak: "Slovenčina",
  czech: "Čeština",
  english: "English",
  textLayer: "Textová vrstva",
  runOcrAgain: "Spustiť OCR znova",
  runOcr: "Spustiť OCR tejto strany",
  rowsReady: (count) => `${count} riadkov je pripravených na úpravu.`,
  recognizedText: "Rozpoznaný text",
  firstOcrInfo: "Prvé OCR môže stiahnuť jazykové modely. Potom ich prehliadač použije z cache.",
  editsAndRows: (edits, rows) => `${edits} úprav · ${rows} OCR riadkov`,
  sourceLabel: (source) => source === "new" ? "nový" : source === "native" ? "PDF text" : source === "ocr" ? "OCR" : "vrstva",
  ocrProgress: (status) => ocrProgressMap("sk", status),
};
